import type { IconProps } from '@carboncopyco/icons';
import type classNames from 'classnames';
import type React from 'react';
import { forwardRef } from 'react';
import type { Colors } from '../../constants';
import type { ButtonBaseProps } from './ButtonBase';
import { ButtonBase } from './ButtonBase';

export interface ButtonProps
  extends Omit<ButtonBaseProps, 'cssStyle' | 'disabled'> {
  id?: string;
  uppercase?: boolean;
  circle?: boolean;
  label?: string;
  disabled?: boolean;
  secondary?: boolean;
  altSecondary?: boolean;
  dark?: boolean;
  darkSecondary?: boolean;
  altSecondaryLink?: boolean;
  altPrimary?: boolean;
  small?: boolean;
  xSmall?: boolean;
  danger?: boolean;
  textColor?: Colors;
  link?: boolean;
  blue?: boolean;
  leftIcon?: React.ReactElement<IconProps>;
  rightIcon?: React.ReactElement<IconProps>;
  icon?: React.ReactElement<IconProps>;
  onClick?: ButtonBaseProps['onClick'];
  submit?: boolean;
  preventDefault?: boolean;
  children?: React.ReactNode;
  noPadding?: boolean;
  highContrast?: boolean;
  faded?: boolean;
  minimalDark?: boolean;
  linkLight?: boolean;
  linkDark?: boolean;
  lightBlueLink?: boolean;
  straightCorners?: ('tl' | 'tr' | 'br' | 'bl')[];
  additionalStyle?: classNames.ArgumentArray;
}

const variants = {
  altPrimary: [
    'border',
    'box-border',
    'border-ds-secondary',
    'text-ds-accent3-100',
    'disabled:text-ds-natural-200',
    'bg-ds-secondary',
    'hover:bg-ds-secondary-400',
    'focus:bg-ds-secondary-400',
    'hover:border-ds-secondary-400',
    'focus:border-ds-secondary-400',
    'active:bg-ds-secondary-500',
    'active:border-ds-secondary-500',
    'disabled:bg-ds-natural-150/25',
    'disabled:border-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  altSecondary: [
    'text-ds-secondary',
    'hover:text-ds-secondary-400',
    'focus:text-ds-secondary-400',
    'active:text-ds-secondary-500',
    'disabled:text-ds-natural-200',
    'bg-transparent',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'active:bg-ds-natural-100',
    'disabled:bg-ds-natural-150/25',
    'border-ds-secondary',
    'hover:border-ds-secondary-400',
    'focus:border-ds-secondary-400',
    'active:border-ds-secondary-500',
    'disabled:border-ds-natural-200/0',
    'border',
    'box-border',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  primary: [
    'text-ds-accent3-100',
    'disabled:text-ds-natural-200',
    'bg-ds-primary',
    'hover:bg-ds-primary-400',
    'focus:bg-ds-primary-400',
    'hover:border-ds-primary-400',
    'focus:border-ds-primary-400',
    'active:bg-ds-primary-500',
    'active:border-ds-primary-500',
    'disabled:bg-ds-natural-150/25',
    'border-ds-primary',
    'border',
    'box-border',
    'disabled:border-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  secondary: [
    'text-ds-primary',
    'hover:text-ds-primary-400',
    'focus:text-ds-primary-400',
    'active:text-ds-primary-500',
    'disabled:text-ds-natural-200',
    'bg-transparent',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'active:bg-ds-natural-100',
    'disabled:bg-ds-natural-150/25',
    'border-ds-primary',
    'hover:border-ds-primary-400',
    'focus:border-ds-primary-400',
    'active:border-ds-primary-500',
    'disabled:border-ds-natural-200/0',
    'border',
    'box-border',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  dark: [
    'border',
    'box-border',
    'text-ds-natural-600',
    'bg-ds-accent3-100',
    'border-ds-accent3-100',
    'hover:bg-ds-natural-100',
    'focus:bg-ds-natural-100',
    'hover:border-ds-natural-100',
    'focus:border-ds-natural-100',
    'active:bg-ds-natural-200',
    'active:border-ds-natural-200',
    'disabled:text-ds-natural-300',
    'disabled:bg-ds-natural-200/20',
    'disabled:border-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  darkSecondary: [
    'border',
    'box-border',
    'text-ds-accent3-100',
    'bg-transparent',
    'border-ds-accent3-100',
    'hover:text-ds-natural-100',
    'focus:text-ds-natural-100',
    'hover:bg-ds-natural-700/10',
    'focus:bg-ds-natural-700/10',
    'hover:border-ds-accent3-100',
    'focus:border-ds-accent3-100',
    'active:bg-ds-natural-700/20',
    'active:text-ds-accent3-100',
    'active:border-ds-accent3-100',
    'disabled:border-ds-accent3-100/0',
    'disabled:bg-ds-natural-200/20',
    'disabled:text-ds-natural-300',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  danger: [
    'text-ds-accent3-100',
    'border',
    'box-border',
    'border-ds-danger',
    'bg-ds-danger',
    'hover:bg-ds-danger-400',
    'focus:bg-ds-danger-400',
    'hover:border-ds-danger-400',
    'focus:border-ds-danger-400',
    'hover:text-ds-accent3-100',
    'focus:text-ds-accent3-100',
    'active:text-ds-accent3-100',
    'active:bg-ds-danger-500',
    'active:border-ds-danger-500',
    'disabled:border-ds-natural-150/25',
    'disabled:bg-ds-natural-150/25',
    'disabled:text-ds-natural-200',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  primaryLink: [
    'text-ds-primary',
    'border',
    'border-transparent',
    'box-border',
    'cursor-pointer',
    'bg-transparent',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'hover:border-ds-natural-50',
    'focus:border-ds-natural-50',
    'hover:text-ds-primary-400',
    'focus:text-ds-primary-400',
    'active:text-ds-primary-500',
    'active:bg-ds-natural-100',
    'active:border-ds-natural-100',
    'disabled:text-ds-natural-200',
    'disabled:bg-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  secondaryLink: [
    'border',
    'border-transparent',
    'box-border',
    'text-ds-natural-600',
    'bg-transparent',
    'cursor-pointer',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'active:text-ds-natural-700',
    'active:bg-ds-natural-100',
    'disabled:text-ds-natural-200',
    'disabled:bg-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  blueLink: [
    'border',
    'border-transparent',
    'box-border',
    'text-ds-primary-300',
    'bg-transparent',
    'cursor-pointer',
    'hover:text-ds-primary-400',
    'focus:text-ds-primary-500',
    'active:text-ds-primary-500',
    'disabled:text-ds-natural-200',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  altSecondaryLink: [
    'border',
    'border-transparent',
    'box-border',
    'text-ds-natural-300',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'hover:text-ds-natural-500',
    'focus:text-ds-natural-500',
    'active:text-ds-secondary-500',
    'active:bg-ds-natural-100',
    'disabled:text-ds-natural-200',
    'disabled:bg-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  secondaryDanger: [
    'text-ds-danger',
    'border',
    'border-ds-danger',
    'box-border',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'hover:text-ds-danger-400',
    'focus:text-ds-danger-400',
    'active:text-ds-danger-500',
    'active:bg-ds-natural-100',
    'disabled:bg-ds-natural-150/25',
    'disabled:border-ds-natural-100/30',
    'disabled:text-ds-natural-200',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  dangerLink: [
    'border',
    'border-transparent',
    'box-border',
    'text-ds-danger',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'hover:text-ds-danger-400',
    'focus:text-ds-danger-400',
    'active:text-ds-danger-500',
    'active:bg-ds-natural-100',
    'disabled:text-ds-natural-200',
    'disabled:bg-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  secondaryDangerLink: [
    'border',
    'border-transparent',
    'box-border',
    'text-ds-danger',
    'hover:bg-ds-natural-50',
    'focus:bg-ds-natural-50',
    'hover:text-ds-danger-400',
    'focus:text-ds-danger-400',
    'active:text-ds-danger-500',
    'active:bg-ds-natural-100',
    'disabled:text-ds-natural-200',
    'disabled:bg-transparent',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  highContrast: [
    'cursor-pointer',
    'text-ds-natural-600',
    'border',
    'border-transparent',
    'box-border',
    'bg-transparent',
    'hover:bg-ds-natural-150',
    'focus:bg-ds-natural-150',
    'active:text-ds-accent3-100',
    'active:bg-ds-natural-700',
    'disabled:text-ds-natural-200',
    'disabled:bg-ds-natural-200-a',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  faded: [
    'border',
    'border-transparent',
    'box-border',
    'bg-transparent',
    'cursor-pointer',
    'hover:opacity-75',
    'focus:opacity-75',
    'active:opacity-50',
    'disabled:opacity-25',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  minimalDark: [
    'border',
    'border-transparent',
    'box-border',
    'bg-ds-accent3-100/0',
    'text-ds-accent3-100',
    'cursor-pointer',
    'hover:bg-ds-accent3-100/25',
    'focus:bg-ds-accent3-100/25',
    'active:bg-ds-accent3-100/50',
    'disabled:bg-ds-accent3-100/0',
    'disabled:text-ds-natural-400',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  linkLight: [
    '!p-0',
    'text-ds-natural-600',
    'bg-transparent',
    'cursor-pointer',
    'hover:text-ds-natural-500',
    'focus:text-ds-natural-500',
    'active:text-ds-natural-400',
    'disabled:text-ds-natural-200',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  linkDark: [
    '!p-0',
    'text-ds-accent3-100',
    'bg-transparent',
    'cursor-pointer',
    'hover:text-ds-accent3-100/75',
    'focus:text-ds-accent3-100/75',
    'active:text-ds-accent3-100/50',
    'disabled:text-ds-accent3-100/25',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
    'disabled:select-none',
  ].join(' '),
  lightBlueLink: [
    'hover:text-ds-natural-600',
    'bg-ds-primary-50',
    'text-ds-primary-300',
    'hover:text-ds-natural-600',
    'disabled:bg-white',
    'disabled:text-ds-natural-300',
    'border',
    'box-border',
    'border-transparent',
  ].join(' '),
} as Record<string, string>;

export const Button = forwardRef(
  (
    {
      id,
      label,
      disabled,
      secondary,
      textColor = 'text-current',
      small,
      xSmall,
      danger,
      link,
      leftIcon,
      rightIcon,
      icon,
      submit,
      onClick,
      uppercase,
      circle,
      preventDefault,
      children,
      altPrimary,
      altSecondary,
      altSecondaryLink,
      dark,
      darkSecondary,
      blue,
      noPadding,
      highContrast,
      faded,
      minimalDark,
      linkLight,
      linkDark,
      straightCorners,
      additionalStyle,
      lightBlueLink,
      ...rest
    }: ButtonProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    const variant = highContrast
      ? 'highContrast'
      : secondary
        ? danger
          ? `secondaryDanger${link ? 'Link' : ''}`
          : `secondary${link ? 'Link' : ''}`
        : danger
          ? `danger${link ? 'Link' : ''}`
          : altPrimary
            ? `altPrimary${link ? 'Link' : ''}`
            : altSecondary
              ? `altSecondary${link ? 'Link' : ''}`
              : dark
                ? `dark${link ? 'Link' : ''}`
                : darkSecondary
                  ? `darkSecondary${link ? 'Link' : ''}`
                  : blue
                    ? `blue${link ? 'Link' : ''}`
                    : faded
                      ? 'faded'
                      : linkLight
                        ? 'linkLight'
                        : linkDark
                          ? 'linkDark'
                          : minimalDark
                            ? 'minimalDark'
                            : lightBlueLink
                              ? 'lightBlueLink'
                              : `primary${link ? 'Link' : ''}`;

    if (!children && ((label && icon) || (!label && !icon))) {
      return null;
    }

    const cssStyle = [variants[variant]];
    if (!noPadding) {
      if (small) {
        cssStyle.push('py-1');
      } else if (xSmall) {
        cssStyle.push('py-ds-3');
      } else {
        cssStyle.push('py-ds-p-7');
      }
    }

    if (icon && !xSmall) {
      cssStyle.push('px-2');
    } else if (icon && xSmall) {
      cssStyle.push('px-ds-3');
    } else {
      !blue && cssStyle.push('px-4');
    }

    if (leftIcon) {
      cssStyle.push('pl-3');
    }
    if (rightIcon) {
      cssStyle.push('pl-4 pr-3');
    }
    if (uppercase) {
      cssStyle.push('uppercase');
    }
    if (circle) {
      cssStyle.push('!rounded-full');
    }
    if (blue) {
      cssStyle.push('px-0');
    }
    if (noPadding) {
      cssStyle.push('p-0');
    }

    return (
      <ButtonBase
        id={id}
        preventDefault={preventDefault}
        ref={ref}
        submit={submit}
        onClick={(e) => onClick?.(e)}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        icon={icon}
        textColor={textColor}
        disabled={!!disabled}
        label={label}
        cssStyle={cssStyle}
        xSmall={xSmall}
        straightCorners={straightCorners}
        additionalStyle={additionalStyle}
        {...rest}
      >
        {children}
      </ButtonBase>
    );
  }
);

Button.displayName = 'Button';
